import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import Loader from "@iso/components/utility/loader.js";
import {
  Button, Input, Select, Empty, Dropdown, Menu, message, Popconfirm, Pagination, Spin,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
  EnvironmentOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  EllipsisOutlined,
  RightOutlined,
} from "@ant-design/icons";
import ContractorPageWrapper from "./Contractor.style";
import { contractorService } from "../../../services/contractor.service";
import { commonHelper } from "@iso/lib/helpers/commonHelper";
import {
  CONTRACTOR_STATUS,
  CONTRACTOR_STATUS_META,
  CONTRACTOR_RISK_META,
  CONTRACTOR_TABS,
  contractorAvatarColor,
  contractorInitials,
} from "@iso/constants/contractor";

/**
 * Contractors list — Phase 1.
 * Workers/assets ratios and compliance score render from the API's
 * aggregate fields, which are zero/null until those sub-entity phases
 * land; the columns show an em-dash rather than fake ratios.
 */
const SEARCH_DEBOUNCE_MS = 400;

const Contractors = () => {
  const history = useHistory();
  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const activePermission = useSelector(
    (state) => state.permission.activePermission
  );
  const activeProperty = commonHelper.getActiveProperty();

  const [loading, setLoading] = useState(true);
  const [restricted, setRestricted] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, expiring_docs: 0, pending_review: 0 });
  const [tabCounts, setTabCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  // Server-side paging state: the table shows exactly the page the backend
  // returns; the stat cards / tab counts come from the whole-property
  // aggregate in the same response.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Full-page loader only for the first fetch; later refetches (tab,
  // filter, page, search) show a table-level spinner so the cards and the
  // search box stay mounted and keep focus.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const canManage =
    activePermission == "owner" || activePermission == "admin";

  // One page of contractors + the whole-property stats, both from the
  // backend. Search / category / risk / tab are now server-side filters
  // (they used to be applied in the browser), so re-run whenever any of
  // them — or the page/page size — changes.
  const fetchData = React.useCallback(() => {
    if (!activePropertyId) return;
    setLoading(true);
    contractorService
      .getByProperty(activePropertyId, {
        status_tab: activeTab === "all" ? undefined : activeTab,
        category: categoryFilter === "all" ? undefined : categoryFilter,
        risk_level: riskFilter === "all" ? undefined : riskFilter,
        search: debouncedSearch || undefined,
        page: currentPage,
        pageSize,
      })
      .then((res) => {
        if (res && res.code === "0000") {
          setRestricted(false);
          setContractors(res.contractors || []);
          setStats(res.stats || {});
          setTabCounts(res.tab_counts || {});
          setCategories(res.categories || []);
          setTotalCount(res.total_count || 0);
        } else if (res && res.code === "0003") {
          // Owner/admin-only module — a member without the role (e.g.
          // deep link) gets a clean notice, not an error toast.
          setRestricted(true);
        } else {
          message.error(res?.message || "Failed to load contractors");
        }
        setLoading(false);
        setHasLoadedOnce(true);
      })
      .catch((err) => {
        console.error("Failed to load contractors:", err);
        message.error("Failed to load contractors");
        setLoading(false);
        setHasLoadedOnce(true);
      });
  }, [
    activePropertyId,
    activeTab,
    categoryFilter,
    riskFilter,
    debouncedSearch,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounce the search box into `debouncedSearch` (which the fetch above
  // depends on) and snap back to page 1 so matches aren't hidden past the
  // end of a stale page.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // White page canvas, same as the Visitor module (restores on unmount).
  useEffect(() => {
    const el = document.querySelector(".isomorphicContent");
    if (!el) return undefined;
    const prev = el.style.background;
    el.style.background = "#ffffff";
    return () => {
      el.style.background = prev;
    };
  }, []);

  // Changing a tab or dropdown filter snaps back to page 1 (batched with
  // the filter change, so the list refetches once) — otherwise a filtered
  // result set could sit past the end of a stale page.
  const changeTab = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
  };
  const changeCategoryFilter = (value) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };
  const changeRiskFilter = (value) => {
    setRiskFilter(value);
    setCurrentPage(1);
  };

  const handleDelete = (c) => {
    contractorService
      .destroy(c.id)
      .then((res) => {
        if (res?.code === "0000") {
          message.success("Contractor deleted");
          fetchData();
        } else {
          message.error(res?.message || "Failed to delete contractor");
        }
      })
      .catch((err) => {
        console.error("Failed to delete contractor:", err);
        message.error("Failed to delete contractor");
      });
  };

  const rowMenu = (c) => (
    <Menu>
      <Menu.Item key="view" onClick={() => history.push(`/contractors/${c.id}`)}>
        View Profile
      </Menu.Item>
      {canManage && (
        <Menu.Item key="delete" danger>
          <Popconfirm
            title="Delete this contractor profile?"
            onConfirm={() => handleDelete(c)}
            okText="Delete"
            cancelText="Cancel"
          >
            Delete
          </Popconfirm>
        </Menu.Item>
      )}
    </Menu>
  );

  const renderRow = (c) => {
    const statusMeta =
      CONTRACTOR_STATUS_META[c.status] ||
      CONTRACTOR_STATUS_META[CONTRACTOR_STATUS.INFORMATION_INCOMPLETE];
    const riskMeta = CONTRACTOR_RISK_META[c.risk_level];
    return (
      <tr key={c.id} onClick={() => history.push(`/contractors/${c.id}`)}>
        <td>
          <div className="ctr-cell">
            <div
              className="ctr-avatar"
              style={{ background: contractorAvatarColor(c.business_name) }}
            >
              {contractorInitials(c.business_name)}
            </div>
            <div>
              <div className="ctr-name">{c.business_name}</div>
              <div className="ctr-sub">
                {[c.primary_contact_name, c.primary_contact_phone]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </div>
            </div>
          </div>
        </td>
        <td>
          {c.category ? (
            <span className="category-badge">{c.category}</span>
          ) : (
            "—"
          )}
        </td>
        <td>
          {riskMeta ? (
            <span className={`risk-badge ${riskMeta.className}`}>
              {riskMeta.label}
            </span>
          ) : (
            "—"
          )}
        </td>
        <td>
          <span className={`status-pill ${statusMeta.className}`}>
            <span className="status-dot" />
            {statusMeta.label}
          </span>
        </td>
        <td>
          {c.compliance_score != null ? `${c.compliance_score}%` : "—"}
        </td>
        <td>
          {c.workers_total > 0
            ? `${c.workers_approved}/${c.workers_total}`
            : "—"}
        </td>
        <td>
          {c.assets_total > 0
            ? `${c.assets_approved}/${c.assets_total}`
            : "—"}
        </td>
        <td>
          {c.is_expiring ? (
            <span className="alert-cell">
              <ClockCircleOutlined /> contract expiring
            </span>
          ) : (
            <span className="alert-clear">Clear</span>
          )}
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            <Button
              className="view-profile-btn"
              icon={<RightOutlined />}
              onClick={() => history.push(`/contractors/${c.id}`)}
            >
              View Profile
            </Button>
            <Dropdown overlay={rowMenu(c)} trigger={["click"]}>
              <Button
                type="text"
                icon={<EllipsisOutlined />}
                style={{ color: "#667085" }}
              />
            </Dropdown>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <LayoutWrapper>
      <ContractorPageWrapper>
        <Box id="contractorBox" className="contractor-box">
          <div className="ctr-breadcrumb">
            <EnvironmentOutlined />
            <span>Property Settings</span>
            <span className="crumb-sep">›</span>
            <span className="crumb-current">Contractor Management</span>
            {activeProperty?.name && (
              <>
                <span className="crumb-sep">·</span>
                <span>{activeProperty.name}</span>
              </>
            )}
          </div>

          <div className="ctr-header">
            <div>
              <h2 className="ctr-title">Contractors</h2>
              <div className="ctr-subtitle">
                Manage contractor businesses, compliance and site access.
              </div>
            </div>
            <div className="ctr-header-actions">
              {canManage && (
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  className="btn-dark-green"
                  onClick={() => history.push("/contractors/new")}
                >
                  Add Contractor
                </Button>
              )}
            </div>
          </div>

          {!hasLoadedOnce ? (
            <Loader />
          ) : restricted ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Contractor management is available to property owners and admins only."
              style={{ padding: "64px 0" }}
            />
          ) : (
            <>
              <div className="stat-cards">
                <div className="stat-card">
                  <div className="stat-icon icon-total"><BankOutlined /></div>
                  <div>
                    <div className="stat-value">{stats.total ?? 0}</div>
                    <div className="stat-label">Total contractors</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon icon-approved"><CheckCircleOutlined /></div>
                  <div>
                    <div className="stat-value">{stats.approved ?? 0}</div>
                    <div className="stat-label">Approved</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon icon-expiring"><ClockCircleOutlined /></div>
                  <div>
                    <div className="stat-value">{stats.expiring_docs ?? 0}</div>
                    <div className="stat-label">Docs expiring soon</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon icon-pending"><WarningOutlined /></div>
                  <div>
                    <div className="stat-value">{stats.pending_review ?? 0}</div>
                    <div className="stat-label">Pending review</div>
                  </div>
                </div>
              </div>

              <div className="ctr-tabs">
                {CONTRACTOR_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`ctr-tab ${activeTab === t.key ? "active" : ""}`}
                    onClick={() => changeTab(t.key)}
                  >
                    {t.label}
                    <span className={`tab-count ${activeTab === t.key ? "active" : ""}`}>
                      {tabCounts[t.key] ?? 0}
                    </span>
                  </button>
                ))}
              </div>

              <div className="filter-bar">
                <Input
                  placeholder="Search contractors…"
                  prefix={<SearchOutlined style={{ color: "#98a2b3" }} />}
                  className="ctr-search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <FilterOutlined className="filter-icon" />
                <Select
                  value={categoryFilter}
                  onChange={changeCategoryFilter}
                  className="filter-select"
                >
                  <Select.Option value="all">All categories</Select.Option>
                  {categories.map((c) => (
                    <Select.Option key={c} value={c}>{c}</Select.Option>
                  ))}
                </Select>
                <Select
                  value={riskFilter}
                  onChange={changeRiskFilter}
                  className="filter-select"
                >
                  <Select.Option value="all">All risk levels</Select.Option>
                  {Object.entries(CONTRACTOR_RISK_META).map(([value, meta]) => (
                    <Select.Option key={value} value={value}>{meta.label}</Select.Option>
                  ))}
                </Select>
              </div>

              <div className="ctr-table-wrap">
                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                    <Spin />
                  </div>
                ) : contractors.length > 0 ? (
                  <>
                    <table className="ctr-table">
                      <thead>
                        <tr>
                          <th>Contractor</th>
                          <th>Category</th>
                          <th>Risk</th>
                          <th>Status</th>
                          <th>Compliance</th>
                          <th>Workers</th>
                          <th>Assets</th>
                          <th>Alerts</th>
                          <th style={{ textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>{contractors.map(renderRow)}</tbody>
                    </table>
                    <div className="pagination-wrap" style={{ display: "flex", justifyContent: "flex-end", padding: "16px 0" }}>
                      <Pagination
                        current={currentPage}
                        total={totalCount}
                        pageSize={pageSize}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger
                        pageSizeOptions={["10", "20", "50", "100"]}
                        onShowSizeChange={(cur, size) => {
                          setCurrentPage(1);
                          setPageSize(size);
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No contractors match your filters."
                    style={{ padding: "48px 0" }}
                  />
                )}
              </div>
            </>
          )}
        </Box>
      </ContractorPageWrapper>
    </LayoutWrapper>
  );
};

export default Contractors;
